import { useMapStateStore } from "@/stores/mapState";
import { useProjectStore } from "@/stores/project";
import { storeToRefs } from "pinia";

// Utility functions for drawing operations
// This file will contain helper functions for DrawingComponent.vue

// Types
export interface GeoPoint {
  latitude: number;
  longitude: number;
}

export interface EdgeType {
  start: GeoPoint;
  end: GeoPoint;
}

export interface CornerCoordinates {
  northwest: { lat: number; lng: number };
  northeast: { lat: number; lng: number };
  southwest: { lat: number; lng: number };
  southeast: { lat: number; lng: number };
}

export interface RealWorldScale {
  x: number;
  y: number;
}

export function calculateLineLength(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  avgScale: number
) {
  const mapStateStore = useMapStateStore();

  // Calculate the length in pixels
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const pixelLength = Math.sqrt(dx * dx + dy * dy);

  // Convert to meters using the average scale and divide by 10 to get correct dimensions
  return pixelLength * avgScale;
}

// Calculate distance between two points using Haversine formula
export function calculateDistanceInMeters(
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (point1.lat * Math.PI) / 180;
  const φ2 = (point2.lat * Math.PI) / 180;
  const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
  const Δλ = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

// Calculate real-world scale based on corner coordinates and canvas dimensions
export function calculateRealWorldScale(
  canvasWidth: number,
  canvasHeight: number
): { avgScale: number; gridSize: number; gridRealSize: number } {
  console.log("calculateRealWorldScale called with:", {
    canvasWidth,
    canvasHeight,
  });

  const mapStateStore = useMapStateStore();

  if (!mapStateStore.screenshot_info) {
    return { avgScale: 0, gridSize: 50, gridRealSize: 0 };
  }

  // Calculate scale (meters per pixel)
  const scale = {
    x: mapStateStore.screenshot_info.dimensions.width / canvasWidth,
    y: mapStateStore.screenshot_info.dimensions.height / canvasHeight,
  };

  // Calculate grid size (approximately 10 meters)
  const gridSize = 50;
  const gridRealSize = scale.x * 50; // Adjust for aspect ratio
  const avgScale = (scale.x + scale.y) / 2;
  //add log here
  console.log("scale", scale, gridSize, gridRealSize);
  return { avgScale, gridSize, gridRealSize };
}

// Calculate canvas size in meters
export function calculateCanvasSizeInMeters(): string {
  const mapStateStore = useMapStateStore();

  if (!mapStateStore.screenshot_info) {
    return "N/A";
  }

  return `${mapStateStore.screenshot_info.dimensions.width.toFixed(
    1
  )} × ${mapStateStore.screenshot_info.dimensions.height.toFixed(1)}`;
}

// Calculate total area in square meters
export function calculateTotalRoofAreaInMeters(
  polygons: Array<Array<{ x: number; y: number }>>,
  obstacles: Array<{ points: Array<{ x: number; y: number }> }>,
  avgScale: number
): string {
  const mapStateStore = useMapStateStore();

  if (!mapStateStore.screenshot_info) {
    return "N/A";
  }

  let totalArea = 0;

  // Calculate area for each polygon
  polygons.forEach((polygon) => {
    const area = calculatePolygonArea(polygon, avgScale);
    totalArea += area;
  });

  // Subtract area of obstacles
  obstacles.forEach((obstacle) => {
    const area = calculatePolygonArea(obstacle.points, avgScale);
    totalArea -= area;
  });

  return totalArea.toFixed(1);
}

// Helper function to calculate polygon area
function calculatePolygonArea(
  points: Array<{ x: number; y: number }>,
  avgScale: number
): number {
  if (!points || points.length < 3) {
    return 0;
  }

  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    // Convert coordinates to meters before calculating area
    const x1 = points[i].x * avgScale;
    const y1 = points[i].y * avgScale;
    const x2 = points[j].x * avgScale;
    const y2 = points[j].y * avgScale;

    area += x1 * y2;
    area -= x2 * y1;
  }

  return Math.abs(area) / 2;
}

// Function to capture the drawing canvas and save to project store
export async function captureDrawingScreenshot() {
  try {
    console.log("Starting to capture drawing screenshot...");
    const projectStore = useProjectStore();
    const { project } = storeToRefs(projectStore);

    // Get the drawing component container
    const drawingContainer = document.querySelector(
      ".drawing-component-container"
    );
    if (!drawingContainer) {
      console.warn("Drawing container not found");
      return false;
    }
    console.log("Found drawing container:", drawingContainer);

    // Create a canvas element
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.warn("Could not get canvas context");
      return false;
    }

    // Calculate dimensions for 85% height and 95% width
    const captureHeight = Math.floor(drawingContainer.clientHeight * 0.85);
    const captureWidth = Math.floor(drawingContainer.clientWidth * 0.93);
    const xOffset = Math.floor(drawingContainer.clientWidth * 0.07); // Skip left 5%

    // Set canvas dimensions to match capture area
    canvas.width = captureWidth;
    canvas.height = captureHeight;

    // Use html2canvas to capture the drawing view
    console.log("Importing html2canvas...");
    const html2canvas = (await import("html2canvas")).default;
    console.log("Capturing canvas...");
    const capturedCanvas = await html2canvas(drawingContainer as HTMLElement, {
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      scale: 1,
      logging: false,
      height: captureHeight,
      width: captureWidth,
      x: xOffset, // Start capture from 5% from the left
    });

    // Create a temporary canvas for compression
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d");
    if (!tempCtx) {
      console.warn("Could not get temp canvas context");
      return false;
    }

    // Set dimensions to 75% of original for compression
    tempCanvas.width = capturedCanvas.width * 0.75;
    tempCanvas.height = capturedCanvas.height * 0.75;

    // Draw the captured canvas onto the temporary canvas with reduced size
    tempCtx.drawImage(
      capturedCanvas,
      0,
      0,
      tempCanvas.width,
      tempCanvas.height
    );

    // Convert to base64 with reduced quality
    const base64Image = tempCanvas.toDataURL("image/jpeg", 0.7); // Using JPEG with 70% quality
    console.log("Screenshot captured and compressed successfully");

    // Update the project's drawing screenshot
    if (project.value) {
      projectStore.setDrawingData({
        ...project.value.drawing_data,
        drawing_screenshot: base64Image,
      });
      console.log("Drawing screenshot updated in project store");
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error capturing drawing screenshot:", error);
    return false;
  }
}
